import {
  EnrollmentInvestmentObjectiveCategory,
  UpdateProfileStatus,
  useCustomerDetails,
  useUpdateSuitability,
} from '@egw/api'
import { Button, Field, ModalDrawer, RadioGroup, useModalState } from '@egw/components'
import { useTranslation } from '@egw/i18n'
import { Controller, useForm } from 'react-hook-form'
import { Logger } from '@egw/logger'
import { type FC, useEffect, useState } from 'react'
import { InformationCircleIcon } from '@heroicons/react/16/solid'
import { SuitabilityField } from '../../types'

const log = new Logger('Investment Goal')

interface InvestmentGoalFormInterface {
  [SuitabilityField.INVESTMENT_OBJECTIVE]: EnrollmentInvestmentObjectiveCategory
}

export const EditInvestmentGoalField: FC = () => {
  const { t } = useTranslation('features-settings')
  const { isOpen, openModal, closeModal } = useModalState()

  const [updateError, setUpdateError] = useState<boolean>(false)

  const { data, refetch, isFetching } = useCustomerDetails()

  const value = data?.customer.investmentObjective

  const { mutate: updateSuitability, isLoading } = useUpdateSuitability({
    onSuccess: (res) => {
      if (res.__typename === 'UpdateProfileError' || res.status === UpdateProfileStatus.FAILED) {
        log.error('Error updating investment goal', { data })
        setUpdateError(true)
      } else if (res.status === UpdateProfileStatus.COMPLETE) {
        void refetch()
          .catch((error: unknown) => {
            log.error(error)
          })
          .finally(() => {
            setUpdateError(false)
            closeModal()
            reset()
          })
      }
    },
    onError: () => {
      setUpdateError(true)
    },
  })

  const defaultValues = {
    [SuitabilityField.INVESTMENT_OBJECTIVE]: value,
  }

  const { handleSubmit, control, reset, formState } = useForm<InvestmentGoalFormInterface>({
    mode: 'all',
    defaultValues,
  })

  useEffect(() => {
    if (data) {
      reset({
        [SuitabilityField.INVESTMENT_OBJECTIVE]: value,
      })
    }
  }, [data, reset, value])

  const onSubmit = async (formData: InvestmentGoalFormInterface): Promise<void> => {
    setUpdateError(false)
    log.debug('Submitting Form', { formData })
    await updateSuitability(formData)
  }

  return value !== undefined ? (
    <>
      <Field
        valueClassName="font-light"
        className="w-full justify-between border-list mb-0 py-4 px-2"
        onEdit={openModal}
        value={t('financial_labels.investment_goal')}
        rightValue={t(`enrollment_investment_objective.${value}`)}
      />
      <ModalDrawer title={t('financial_labels.investment_goal')} isOpen={isOpen} onClose={closeModal}>
        <form
          id="updateInvestmentGoalForm"
          className="flex flex-col gap-4 h-full md:h-screen justify-between"
          onSubmit={(e) => {
            e.preventDefault()
            void handleSubmit(onSubmit)()
          }}
        >
          <div className="flex flex-col gap-10">
            <div>
              <p className="mt-4 mb-4 font-semibold">{t('update_investment_goal.your_investment_goal')}</p>
              <Controller
                name={SuitabilityField.INVESTMENT_OBJECTIVE}
                control={control}
                render={({ field }) => (
                  <RadioGroup
                    {...field}
                    value={field.value}
                    options={Object.values(EnrollmentInvestmentObjectiveCategory).map((item) => {
                      return {
                        label: t(`enrollment_investment_objective.${item}`),
                        value: item,
                      }
                    })}
                  />
                )}
              />
            </div>
            {updateError ? (
              <div className="flex bg-alertErrorBg p-3 rounded-md">
                <InformationCircleIcon className="w-6 h-6 fill-red-500" />
                <p className="text-sm text-alertErrorText ml-2">{t('common.update_error')} </p>
              </div>
            ) : null}
          </div>
          <Button
            id="updateInvestmentGoal"
            type="submit"
            isLoading={isLoading || isFetching}
            isDisabled={!formState.isValid}
          >
            {t('common.confirm')}
          </Button>
        </form>
      </ModalDrawer>
    </>
  ) : null
}
